import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Loader2, FileText, ArrowLeft } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";

const POLICY_MAP = {
  "refund-policy": {
    key: "refund_policy_html",
    overline: "Zion Christian Academy",
    title: "Refund Policy",
    subtitle: "Terms governing tuition and record fees.",
    testid: "refund-policy-page",
  },
  "enrollment-policy": {
    key: "enrollment_policy_html",
    overline: "Zion Christian Academy",
    title: "Enrollment Policy",
    subtitle: "The complete terms of enrollment at ZCA.",
    testid: "enrollment-policy-page",
  },
};

export default function PolicyPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+/, "").split("/")[0];
  const meta = POLICY_MAP[slug];
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  if (!meta) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex items-center justify-center text-ink/70">
          Policy not found.
        </div>
        <SiteFooter />
      </div>
    );
  }

  const html = settings?.[meta.key] || "";
  const updatedAt = settings?.policies_updated_at;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid={meta.testid}>
      <SiteHeader />

      <section className="bg-purple-dark text-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
          <div className="text-xs tracking-[0.22em] uppercase text-gold-light font-bold mb-3 flex items-center gap-2">
            <FileText size={13} /> {meta.overline}
          </div>
          <h1 className="h-serif text-4xl sm:text-5xl tracking-tight mb-3">
            {meta.title}
          </h1>
          <p className="text-white/85 leading-relaxed">{meta.subtitle}</p>
          {updatedAt && (
            <p className="text-xs text-white/60 mt-4">
              Last updated {new Date(updatedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          )}
        </div>
      </section>

      <section className="max-w-3xl w-full mx-auto px-5 sm:px-8 py-14 flex-1">
        {settings === null ? (
          <div className="flex items-center gap-2 text-ink/60"><Loader2 size={16} className="animate-spin" /> Loading policy…</div>
        ) : !html ? (
          <div className="border border-grey bg-grey-light p-8 text-center text-ink/70 text-sm">
            This policy is being finalized. Please contact the school directly at{" "}
            <a href={`mailto:${settings?.contact_email || "zca.k.12.26@outlook.com"}`} className="text-purple hover:text-purple-dark font-bold">{settings?.contact_email || "zca.k.12.26@outlook.com"}</a>.
          </div>
        ) : (
          <article
            className="policy-content"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
        <div className="mt-16 pt-8 border-t border-grey">
          <Link to="/" className="text-sm text-purple hover:text-purple-dark inline-flex items-center gap-1" data-testid="policy-back-home">
            <ArrowLeft size={14} /> Return home
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
